import text from '../text.json'
import Input from '../../../components/core/input'
import {SimpleBlueButton} from '../../../components/core/button'
import ButtonLoader from '../../../components/core/button/ButtonLoader'
import {Formik} from 'formik'
import {useEffect, useState} from 'react'
import {channelTypeSchema, webhookSchema} from '../../../utils/validation'
import {addChannel} from '../../../stores/customer-settings'
import {useDispatch, useSelector} from 'react-redux'
import channelTypes from '../../../data/channelTypes.json'
import TextHelper, {trimStringValues} from '../../../utils/text'
import {toast} from 'react-toastify'
import LimitManager from '../../../utils/limitManager'
import CustomSelect from '../../../components/core/customSelect'
import {routes} from '../../../routes'
import {SimpleDiscordIcon, MSIcon, SlackIcon, WebhookIcon} from '../../../icons'
import localStorageKeys from '../../../utils/localStorageKeys'
import RenderedForPaid from '../../../components/pages/RenderedForPaid'
import plans from '../../../data/plans.json'
import usageLimits from '../../../data/usageLimits.json'
import limitUsageManager from '../../../utils/hooks/limitUsageManager'

const AddTeamChannel = ({closePopup, webhooks, type, teamId}) => {
    const [isProcessing, setIsProcessing] = useState(false)
    const [schema, setSchema] = useState(channelTypeSchema)
    const [active, setActive] = useState(null)
    const currentPlan = localStorage.getItem(localStorageKeys.PLAN)
    const isWebhookChannelDisabled = !LimitManager.getWebhookChannelIsEnabled()
    const isSlackChannelDisabled = !LimitManager.getSlackChannelIsEnabled()
    const isMSTeamChannelDisabled = !LimitManager.getMSTeamsChannelIsEnabled()
    const isDiscordChannelDisabled = !LimitManager.getDiscordChannelIsEnabled()
    const slackBaseUrl = 'https://slack.com'
    const discordBaseUrl = 'https://discord.com'
    const organisationLimitUsages = useSelector(state => state.auth.organisationLimitUsages)
    const getChannelLimit = (limitKey) => {
        const {value, usage} = limitUsageManager(
            usageLimits.limit_modules.incident_management.module,
            limitKey,
            organisationLimitUsages
        )
        return {value, usage}
    }

    const createTypeConfig = (limitKey) => ({
        limit: getChannelLimit(limitKey)?.value,
        usage: getChannelLimit(limitKey)?.usage,
        key: limitKey
    })

    const types = {
        [channelTypes.types.webhook]: createTypeConfig(
            usageLimits.limit_keys.per_user_webhook_channel_count.limit_key
        ),
        [channelTypes.types.slack]: createTypeConfig(
            usageLimits.limit_keys.slack_channel_count.limit_key
        ),
        [channelTypes.types.ms_teams]: createTypeConfig(
            usageLimits.limit_keys.ms_teams_channel_count.limit_key
        ),
        [channelTypes.types.discord]: createTypeConfig(
            usageLimits.limit_keys.discord_channel_count.limit_key
        )
    }

    const dispatch = useDispatch()

    const handleInfo = (index, state) => {
        state ? setActive(index) : setActive(null)
    }

    const channelTypeElement = (value, label, isDisabled, icon) => {
        return {value, label, isDisabled, icon}
    }

    const channelTypeOptions = [
        channelTypeElement(channelTypes.types.webhook, 'Webhook', isWebhookChannelDisabled, <WebhookIcon className="filled-icon"/>),
        channelTypeElement(channelTypes.types.slack, 'Slack', isSlackChannelDisabled, <SlackIcon/>),
        channelTypeElement(channelTypes.types.ms_teams, 'Microsoft teams', isMSTeamChannelDisabled, <MSIcon className="ms-icon"/>),
        channelTypeElement(channelTypes.types.discord, 'Discord', isDiscordChannelDisabled, <SimpleDiscordIcon/>)
    ]

    const [initialFormValues, setInitialFormValues] = useState({
        type: '',
        value: ''
    })

    const fieldNames = {
        channel_type: 'type',
        channel_value: 'value'
    }

    const isSameType = (value, name) => value === name

    const getSlackAuthenticationUrl = () => {
        const clientId = process.env.REACT_APP_SLACK_CLIENT_ID
        const redirectUri = process.env.REACT_APP_BASE_URL + routes.team_channels.path.replace('/', '')
        const scope = 'incoming-webhook,users:read.email,users:read'
        return `${slackBaseUrl}/oauth/v2/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&scope=${scope}`
    }

    const getDiscordAuthenticationUrl = () => {
        const clientId = process.env.REACT_APP_DISCORD_CLIENT_ID
        const redirectUri = `${process.env.REACT_APP_BASE_URL + routes.team_channels.path.replace('/', '')}?channel_type=discord&response_type=code`
        const permissions = '0'
        const scope = 'identify guilds bot webhook.incoming'
        return `${discordBaseUrl}/oauth2/authorize?client_id=${clientId}&permissions=${permissions}&redirect_uri=${redirectUri}&scope=${scope}`
    }

    const addTeamCahnnel = async(values, {setFieldError, resetForm}) => {
        setIsProcessing(true)
        if (isSameType(values.type.value, channelTypes.types.slack)) {
            window.location.href = getSlackAuthenticationUrl()
            return
        }
        if (isSameType(values.type.value, channelTypes.types.discord)) {
            window.location.href = getDiscordAuthenticationUrl()
            return
        }
        if (webhooks.some(item => item.new_value === values.value)) {
            toast.error(text.webhook_already_used)
            setIsProcessing(false)
            return
        }
        const success = await dispatch(addChannel(true, teamId, trimStringValues({
            ...values,
            type: values.type.value
        }), setFieldError, resetForm))
        setIsProcessing(false)
        if (success) {
            closePopup()
        }
    }

    const getPaidText = values => {
        const value = values.type.value
        let data = {}

        Object.keys(types).forEach(item => {
            if (isSameType(value, item)) {
                data = {
                    channelLimit: types[item].limit,
                    channelCount: types[item]?.limit - types[item]?.usage
                }
            }
        })

        return data.channelCount > 0 && text.for_paid_channel
            .replace(':channel_limit', data.channelLimit)
            .replace(':channel_type', values.type.label)
            .replace(':channel_count', data.channelCount)
    }

    const isExceeded = values => {
        const value = values.type.value
        let condition
        Object.keys(types).forEach(item => {
            if (isSameType(value, item)) {
                condition = types[item].limit === types[item].usage
            }
        })

        return condition
    }

    const checkSchema = (name, data, setFieldValue) => {
        setFieldValue(name, data)
        if (data.value === channelTypes.types.slack || data.value === channelTypes.types.discord) {
            setSchema(channelTypeSchema)
            return
        }
        setSchema(webhookSchema)
    }
    useEffect(() => {
        if (type) {
            setInitialFormValues(prev => ({
                ...prev,
                type: channelTypeOptions.find(item => item.value === type)
            }))
            setSchema(type === channelTypes.types.slack || type === channelTypes.types.discord ? channelTypeSchema : webhookSchema)
        }
    }, [type])

    return (
        <Formik initialValues={initialFormValues}
            enableReinitialize={true}
            onSubmit={addTeamCahnnel}
            validationSchema={schema}
            validateOnBlur={false}
            validateOnChange={false}>
            {({values, errors, handleSubmit, setFieldValue, handleChange}) => (
                <form onSubmit={handleSubmit} className="modal-body">
                    {values.type.value &&
                        <RenderedForPaid forPaidText={getPaidText(values) || text.for_paid_general} isExceeded={isExceeded(values)} isTextWithLimits={!!getPaidText(values)}/>}
                    <CustomSelect
                        placeholder={text.channel_type}
                        onChange={(name, data) => checkSchema(name, data, setFieldValue)}
                        infoText={text.webhook_type_info}
                        name={fieldNames.channel_type}
                        value={values.type}
                        options={channelTypeOptions}
                        defaultFirst={true}
                        checkboxClassName="multiselect"
                        activeInfo={active === fieldNames.channel_type}
                        handleInfo={(state) => handleInfo(fieldNames.channel_type, state)}
                        error={errors.type}
                    />
                    {isSameType(values.type.value, channelTypes.types.webhook) && (
                        <Input
                            type="url"
                            value={values.value}
                            inputIsDisabled={isExceeded(values)}
                            placeholder={text.webhook}
                            name={fieldNames.channel_value}
                            onChange={e => {
                                handleChange(e)
                                setFieldValue('value', TextHelper.getNormalizedUrl(e.target.value))
                            }}
                            infoText={text.webhook_input_info}
                            activeInfo={active === fieldNames.channel_value}
                            handleInfo={(state) => handleInfo(fieldNames.channel_value, state)}
                            error={errors.value}
                        />
                    )}
                    {isSameType(values.type.value, channelTypes.types.ms_teams) && (
                        <Input
                            type="url"
                            value={values.value}
                            inputIsDisabled={isExceeded(values)}
                            placeholder={text.ms_teams_url}
                            name={fieldNames.channel_value}
                            onChange={handleChange}
                            infoText={text.ms_teams_input_info}
                            activeInfo={active === fieldNames.channel_value}
                            handleInfo={(state) => handleInfo(fieldNames.channel_value, state)}
                            error={errors.value}
                        />
                    )}
                    {(isWebhookChannelDisabled || isSlackChannelDisabled || isMSTeamChannelDisabled || isDiscordChannelDisabled) &&
                        <RenderedForPaid forPaidText={text.for_paid} isExceeded={false}/>}
                    <SimpleBlueButton
                        type="submit"
                        disabled={isProcessing || isExceeded(values) || currentPlan === plans.free_v2.key}>
                        {isProcessing && <ButtonLoader/>}
                        {(values.type.value === channelTypes.types.slack || values.type.value === channelTypes.types.discord) ? text.add : text.submit}
                    </SimpleBlueButton>
                </form>
            )}
        </Formik>
    )
}

export default AddTeamChannel
